/**
 * RecoveryCodes
 *
 * Shows a set of MFA recovery codes once, with a copy button and a confirmation
 * that the user has saved them. Used in two places:
 *
 * - User/Profile/TwoFactorAuth, when the authenticator app is first verified
 * - Auth/LoginForm, when signing in with the last remaining code earns a
 *   fresh set
 *
 * The caller owns the description and the action label; everything else is
 * common to both.
 */

import React from "react";
import { Button, Form, Col } from "react-bootstrap";

import Icon from "components/Icons/Icon";

import { withT } from "services/translation/";

class RecoveryCodes extends React.Component {
  state = {
    copied: false,
    downloaded: false,
    confirmed: false,
  };

  copyCodes = () => {
    const text = this.props.codes.join("\n");
    const copied = () => this.setState({ copied: true });

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(copied, () => {});
      return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    try {
      if (document.execCommand("copy")) copied();
    } catch (e) {
      // Clipboard unavailable - the codes are still on screen to copy by hand
    }
    document.body.removeChild(textArea);
  };

  downloadCodes = () => {
    const text = this.props.codes.join("\n");
    const downloaded = () => this.setState({ downloaded: true });

    const anchor = document.createElement("a");
    const file = new Blob([text], {type: 'text/plain'});
    anchor.href = URL.createObjectURL(file);
    anchor.download = "conxtd-recovery-codes.txt";
    document.body.appendChild(anchor);
    try {
      anchor.click();
      downloaded();
    } catch (e) {}
    document.body.removeChild(anchor);
  }

  render() {
    const {
      codes,
      description,
      actionLabel,
      onContinue,
      className,
      style,
      t
    } = this.props;
    const { copied, downloaded, confirmed } = this.state;

    if (!codes || codes.length === 0) return null;

    return (
      <div
        className={className}
        // The inset panel styling suits an inline placement; a caller that
        // already provides its own surface (a modal body) overrides it
        style={{ background: "#f8f9fa", borderRadius: "6px", ...style }}
      >
        <div className="mb-2">
          <strong>{t("title")}</strong>
        </div>
        <div className="mb-2">{description}</div>
        <div className="mb-3 text-danger">{t("warning")}</div>

        {/* Column count has to follow this panel's own width rather than the
            viewport - the panel is a good deal narrower than the window, so
            Bootstrap's breakpoints leave three columns in place long after a
            code stops fitting in one. auto-fit against a min track wide enough
            for a code steps 3 -> 2 -> 1 columns as the panel narrows; nowrap
            makes sure a code is never broken across lines. maxWidth caps it at
            three columns so six codes stay a tidy 3 x 2. */}
        <div
          className="mb-3"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(12.5rem, 1fr))",
            gap: "0.5rem 1.5rem",
            maxWidth: "41rem"
          }}
        >
          {codes.map(code => (
            <code
              key={code}
              style={{
                fontSize: "1rem",
                letterSpacing: "0.03em",
                userSelect: "all",
                whiteSpace: "nowrap"
              }}
            >
              {code}
            </code>
          ))}
        </div>

        <Form.Row className="justify-content-end align-items-center">
          <Col md="auto">
            <Button variant="secondary" type="button" onClick={this.copyCodes}>
              <Icon className="fas fa-clipboard" />
              <span className="ml-2">{copied ? t("copied") : t("copy_button")}</span>
            </Button>
          </Col>
          <Col md="auto">
            <Button variant="secondary" type="button" onClick={this.downloadCodes}>
              <Icon className="fas fa-download" />
              <span className="ml-2">{downloaded ? t("downloaded") : t("download_button")}</span>
            </Button>
          </Col>
        </Form.Row>
        
        <Form.Row className="mt-3 justify-content-between align-items-center">
          <Col md="auto">
            <Form.Check
              type="checkbox"
              id="recovery-codes-saved"
              label={t("confirm")}
              checked={confirmed}
              onChange={e => this.setState({ confirmed: e.target.checked })}
            />
          </Col>
          <Col md="auto">
            <Button
              variant="primary"
              type="button"
              disabled={!confirmed}
              onClick={onContinue}
            >
              {actionLabel || t("continue_button")}
            </Button>
          </Col>
        </Form.Row>
      </div>
    );
  }
}

RecoveryCodes.defaultProps = {
  className: "p-3",
};

export default withT(RecoveryCodes, "common.recovery_codes");
